import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, Globe, MapPin, Upload, Save, Plus, Trash2, Loader, Building, Database, Shield, CheckCircle, AlertTriangle, FileText } from 'lucide-react';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  onSnapshot 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage, auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext'; // Import enhanced AuthContext

const MyAccount = () => {
  // Get multi-tenant context
  const { 
    currentUser, 
    currentCompany, 
    getCompanyContext, 
    authInitialized 
  } = useAuth();

  // Initialize state with empty values or default values
  const [profileData, setProfileData] = useState({
    profilePicture: null,
    profilePictureRef: null, // Store Firebase Storage reference
    rentalName: '',
    rentalAddress: '',
    businessRegistrationNumber: '',
    phoneNumbers: [''],
    emails: [''],
    websites: ['']
  });
  
  const [previewImage, setPreviewImage] = useState(null);
  const [isSaving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [connectionStatus, setConnectionStatus] = useState('connecting');

  // Get current company context
  const companyId = getCompanyContext();

  // Get current user ID with multi-tenant support
  const getUserId = () => {
    if (auth.currentUser) {
      return auth.currentUser.uid;
    }
    // Fallback using current user from AuthContext
    if (currentUser?.id) {
      return currentUser.id;
    }
    // Ultimate fallback for demo purposes
    return 'demo_user';
  };

  // Generate company-specific document ID
  const getCompanyUserDocId = () => {
    const userId = getUserId();
    return `${companyId}_${userId}`;
  };

  // Generate company-specific storage path
  const getCompanyStoragePath = (fileName) => {
    return `profilePictures/${companyId}/${fileName}`;
  };

  // Load profile data from Firestore with multi-tenant support
  const loadProfileData = async () => {
    if (!companyId) {
      console.log('No company context available');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setConnectionStatus('connecting');
      
      const docId = getCompanyUserDocId();
      const docRef = doc(db, 'userProfiles', docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log("Loaded multi-tenant profile data from Firebase:", data);
        
        // Ensure arrays exist and verify company context
        const profileData = {
          profilePicture: data.profilePicture || null,
          profilePictureRef: data.profilePictureRef || null,
          rentalName: data.rentalName || '',
          rentalAddress: data.rentalAddress || '',
          businessRegistrationNumber: data.businessRegistrationNumber || '',
          phoneNumbers: data.phoneNumbers || [''],
          emails: data.emails || [''],
          websites: data.websites || [''],
          companyId: data.companyId || companyId,
          companyName: data.companyName || currentCompany?.name,
          ...data
        };
        
        // Verify data belongs to current company
        if (profileData.companyId !== companyId) {
          console.warn('Profile data company mismatch, creating new profile');
          setProfileData(getDefaultProfileData());
        } else {
          setProfileData(profileData);
          
          // Set preview image if exists
          if (profileData.profilePicture) {
            setPreviewImage(profileData.profilePicture);
          }
        }
      } else {
        console.log("No profile data found for company, using defaults");
        setProfileData(getDefaultProfileData());
      }
      
      setConnectionStatus('connected');
    } catch (error) {
      console.error("Error loading multi-tenant profile data:", error);
      setConnectionStatus('error');
      setMessage({ 
        text: `Error loading profile data for ${currentCompany?.name}. Please try again.`, 
        type: 'error' 
      });
      
      // Fallback to company-specific localStorage
      loadFromCompanyLocalStorage();
    } finally {
      setIsLoading(false);
    }
  };

  // Get default profile data with company context
  const getDefaultProfileData = () => {
    return {
      profilePicture: null,
      profilePictureRef: null,
      rentalName: currentCompany?.name || '',
      rentalAddress: '',
      businessRegistrationNumber: '',
      phoneNumbers: [''],
      emails: [''],
      websites: [''],
      companyId: companyId,
      companyName: currentCompany?.name || 'Unknown Company'
    };
  };

  // Fallback to company-specific localStorage
  const loadFromCompanyLocalStorage = () => {
    try {
      const storageKey = `userProfile_${companyId}_${getUserId()}`;
      const savedData = localStorage.getItem(storageKey);
      
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setProfileData({
          ...getDefaultProfileData(),
          ...parsedData
        });
        
        if (parsedData.profilePicture) {
          setPreviewImage(parsedData.profilePicture);
        }
        
        console.log(`Loaded profile from company-specific localStorage for ${currentCompany?.name}`);
      }
    } catch (error) {
      console.error('Error loading from company localStorage:', error);
    }
  };

  // Save to company-specific localStorage as backup
  const saveToCompanyLocalStorage = (data) => {
    try {
      const storageKey = `userProfile_${companyId}_${getUserId()}`;
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to company localStorage:', error);
    }
  };

  // Save profile data to Firestore with multi-tenant support
  const saveProfileData = async (dataToSave) => {
    if (!companyId) {
      throw new Error('No company context available');
    }

    try {
      const userId = getUserId();
      const docId = getCompanyUserDocId();
      const docRef = doc(db, 'userProfiles', docId);
      
      const saveData = {
        ...dataToSave,
        companyId: companyId,
        companyName: currentCompany?.name || 'Unknown Company',
        userId: userId,
        updatedAt: new Date(),
        tenantIsolated: true
      };
      
      console.log("Saving multi-tenant profile data to Firebase:", saveData);
      
      // Use setDoc to create or update the document
      await setDoc(docRef, saveData, { merge: true });
      
      // Also save to localStorage as backup
      saveToCompanyLocalStorage(saveData);
      
      return true;
    } catch (error) {
      console.error("Error saving multi-tenant profile data:", error);
      throw error;
    }
  };

  // Upload image to Firebase Storage with multi-tenant path
  const uploadImageToStorage = async (file) => {
    if (!companyId) {
      throw new Error('No company context available for image upload');
    }

    try {
      setIsUploading(true);
      const userId = getUserId();
      const timestamp = Date.now();
      const fileName = `profile_${userId}_${timestamp}.${file.name.split('.').pop()}`;
      const storagePath = getCompanyStoragePath(fileName);
      const storageRef = ref(storage, storagePath);
      
      console.log("Uploading image to company-specific Firebase Storage:", storagePath);
      
      // Delete old profile picture if exists
      if (profileData.profilePictureRef) {
        try {
          const oldRef = ref(storage, profileData.profilePictureRef);
          await deleteObject(oldRef);
          console.log("Deleted old profile picture");
        } catch (deleteError) {
          console.log("Could not delete old image (might not exist):", deleteError);
        }
      }
      
      // Upload new image
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log("Image uploaded successfully to company storage:", downloadURL);
      
      return {
        url: downloadURL,
        ref: snapshot.ref.fullPath
      };
    } catch (error) {
      console.error("Error uploading image to company storage:", error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  // Handle profile picture upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!companyId) {
      setMessage({ text: 'No company context available', type: 'error' });
      return;
    }

    // Check file type
    if (!file.type.match('image.*')) {
      setMessage({ text: 'Please select an image file', type: 'error' });
      return;
    }

    // Check file size - 5MB max
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ text: 'File size should be less than 5MB', type: 'error' });
      return;
    }

    // Create preview first
    const reader = new FileReader();
    reader.onload = async (event) => {
      // Create an image element to check dimensions
      const img = new Image();
      img.onload = async () => {
        // Check if image is at least 1200x1200
        if (img.width < 1200 || img.height < 1200) {
          setMessage({ 
            text: 'Image should be at least 1200px x 1200px', 
            type: 'error' 
          });
          return;
        }
        
        try {
          // Set preview immediately
          setPreviewImage(event.target.result);
          
          // Upload to company-specific Firebase Storage
          const uploadResult = await uploadImageToStorage(file);
          
          // Update profile data with new image URL and reference
          setProfileData(prev => ({
            ...prev,
            profilePicture: uploadResult.url,
            profilePictureRef: uploadResult.ref
          }));
          
          setMessage({ text: `Image uploaded successfully for ${currentCompany?.name}`, type: 'success' });
          
        } catch (error) {
          console.error("Error handling image upload:", error);
          setMessage({ text: 'Error uploading image. Please try again.', type: 'error' });
          // Reset preview on error
          setPreviewImage(profileData.profilePicture);
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Add a new field (phone, email, website)
  const addField = (fieldType) => {
    console.log(`Adding new ${fieldType} for company ${currentCompany?.name}`);
    setProfileData(prev => ({
      ...prev,
      [fieldType]: [...prev[fieldType], '']
    }));
  };

  // Remove a field by index
  const removeField = (fieldType, index) => {
    if (profileData[fieldType].length <= 1) return;
    
    console.log(`Removing ${fieldType} at index ${index} for company ${currentCompany?.name}`);
    setProfileData(prev => ({
      ...prev,
      [fieldType]: prev[fieldType].filter((_, i) => i !== index)
    }));
  };

  // Update field value
  const updateField = (fieldType, index, value) => {
    console.log(`Updating ${fieldType} at index ${index} to "${value}" for company ${currentCompany?.name}`);
    const updatedFields = [...profileData[fieldType]];
    updatedFields[index] = value;
    
    setProfileData(prev => ({
      ...prev,
      [fieldType]: updatedFields
    }));
  };

  // Update simple fields (name, address, businessRegistrationNumber)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Updating ${name} to "${value}" for company ${currentCompany?.name}`);
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save all data to Firestore
  const saveProfile = async () => {
    if (!companyId) {
      setMessage({ text: 'No company context available', type: 'error' });
      return;
    }

    setSaving(true);
    
    // Validation
    if (!profileData.rentalName?.trim()) {
      setMessage({ text: 'Rental Name is required', type: 'error' });
      setSaving(false);
      return;
    }
    
    try {
      console.log("Saving multi-tenant profile data for company:", currentCompany?.name);
      
      await saveProfileData(profileData);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('profileUpdated', {
        detail: { companyId, companyName: currentCompany?.name }
      }));
      
      setMessage({ text: `Profile saved successfully for ${currentCompany?.name}!`, type: 'success' });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage(prev => prev.type === 'success' ? { text: '', type: '' } : prev);
      }, 3000);
      
    } catch (error) {
      console.error("Error saving multi-tenant profile data:", error);
      setMessage({ text: 'Error saving profile data. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Setup real-time listener for profile updates with multi-tenant support
  useEffect(() => {
    if (!authInitialized || !companyId) {
      return;
    }

    const docId = getCompanyUserDocId();
    const docRef = doc(db, 'userProfiles', docId);
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        console.log("Real-time multi-tenant profile data update:", data);
        
        // Verify data belongs to current company
        if (data.companyId === companyId) {
          // Update local state with new data
          const updatedProfileData = {
            profilePicture: data.profilePicture || null,
            profilePictureRef: data.profilePictureRef || null,
            rentalName: data.rentalName || '',
            rentalAddress: data.rentalAddress || '',
            businessRegistrationNumber: data.businessRegistrationNumber || '',
            phoneNumbers: data.phoneNumbers || [''],
            emails: data.emails || [''],
            websites: data.websites || [''],
            companyId: data.companyId,
            companyName: data.companyName,
            ...data
          };
          
          setProfileData(updatedProfileData);
          
          if (updatedProfileData.profilePicture) {
            setPreviewImage(updatedProfileData.profilePicture);
          }
        }
      }
    }, (error) => {
      console.error("Error in real-time listener:", error);
      setConnectionStatus('error');
    });

    // Initial load
    loadProfileData();

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [authInitialized, companyId, currentCompany]);

  console.log("Current multi-tenant profileData:", profileData);

  // Loading state
  if (isLoading || !authInitialized) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading multi-tenant profile...</p>
            {currentCompany && (
              <p className="text-gray-500 text-sm mt-2">Company: {currentCompany.name}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No company context available
  if (!companyId || !currentCompany) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-20 w-20 text-yellow-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">No Company Context</h3>
          <p className="text-gray-500 text-lg">Please ensure you're logged in with a valid company account.</p>
        </div>
      </div>
    );
  }

  // Connection status indicator
  const ConnectionStatus = () => {
    const statusConfig = {
      connecting: { 
        color: 'text-yellow-400', 
        icon: Loader, 
        text: 'Connecting to Firebase...', 
        bgColor: 'bg-yellow-900/20',
        borderColor: 'border-yellow-700',
        spin: true 
      },
      connected: { 
        color: 'text-green-400', 
        icon: Database, 
        text: 'Connected to Firebase', 
        bgColor: 'bg-green-900/20',
        borderColor: 'border-green-700',
        spin: false 
      },
      error: { 
        color: 'text-red-400', 
        icon: AlertTriangle, 
        text: 'Firebase Connection Error', 
        bgColor: 'bg-red-900/20',
        borderColor: 'border-red-700',
        spin: false 
      }
    };

    const { color, icon: Icon, text, bgColor, borderColor, spin } = statusConfig[connectionStatus];

    return (
      <div className={`flex items-center gap-2 ${color} ${bgColor} ${borderColor} border rounded-lg px-3 py-2 mb-4`}>
        <Icon size={16} className={spin ? 'animate-spin' : ''} />
        <div className="flex flex-col">
          <span className="text-xs font-medium">{text}</span>
          <span className="text-xs opacity-75">
            Multi-tenant mode • {currentCompany.name}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-4">My Account</h2>
        <p className="text-gray-400">Manage your rental business profile information</p>
        <div className="flex items-center gap-2 text-blue-400 text-sm mt-2">
          <Building size={16} />
          <span>{currentCompany.name}</span>
          <span className="text-gray-500">•</span>
          <Database size={16} />
          <span>Multi-Tenant Firebase</span>
        </div>
      </div>

      {/* Connection Status */}
      <ConnectionStatus />
      
      {/* Multi-Tenant Firebase Connection Status */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3 mb-6">
        <div className="bg-blue-500/20 p-2 rounded-lg">
          <Shield size={20} className="text-blue-400" />
        </div>
        <div>
          <h4 className="text-white font-medium mb-1 flex items-center gap-2">
            Multi-Tenant Profile Management
            <CheckCircle size={16} className="text-green-400" />
          </h4>
          <p className="text-gray-300 text-sm mb-2">
            Your profile data is isolated to <strong>{currentCompany.name}</strong> and automatically synced with Firebase Firestore.
            Profile pictures are stored securely in company-specific Firebase Storage paths.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-blue-200 mt-3">
            <div>
              <span className="font-medium">Company ID:</span>
              <div className="font-mono">{companyId}</div>
            </div>
            <div>
              <span className="font-medium">Document ID:</span>
              <div className="font-mono">{getCompanyUserDocId()}</div>
            </div>
            <div>
              <span className="font-medium">Data Isolation:</span>
              <div className="text-green-300">✅ Active</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Message display */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-800/30 border border-green-700' : 
          'bg-red-800/30 border border-red-700'
        }`}>
          <p className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
            {message.text}
          </p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Profile picture upload */}
        <div className="md:col-span-1">
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
            <div className="border-b border-gray-700 px-4 py-3">
              <h3 className="font-medium text-white">Profile Picture</h3>
              <p className="text-xs text-gray-400 mt-1">
                Stored in company-specific Firebase Storage
              </p>
              <div className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                <Building size={12} />
                {currentCompany.name}
              </div>
            </div>
            <div className="p-4 flex flex-col items-center space-y-4">
              <div className="relative w-full aspect-square bg-gray-700 rounded-lg overflow-hidden border-2 border-dashed border-gray-600 flex items-center justify-center">
                {isUploading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                    <div className="text-center">
                      <Loader className="animate-spin h-8 w-8 text-orange-500 mx-auto mb-2" />
                      <p className="text-white text-sm">Uploading to {currentCompany.name}...</p>
                    </div>
                  </div>
                )}
                {previewImage ? (
                  <img 
                    src={previewImage} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={64} className="text-gray-500" />
                )}
              </div>
              
              <div className="text-center text-sm text-gray-400 mt-2">
                <p>Recommended size: 1200px x 1200px</p>
                <p>Max file size: 5MB</p>
                <p className="text-xs text-blue-400 mt-1">
                  Isolated to {currentCompany.name}
                </p>
              </div>
              
              <label className="w-full">
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleImageUpload}
                  disabled={isUploading || isSaving || !companyId}
                />
                <div className={`w-full py-2 px-4 rounded-lg text-gray-300 flex items-center justify-center gap-2 cursor-pointer transition-colors ${
                  isUploading || isSaving || !companyId
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}>
                  {isUploading ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      <span>{previewImage ? 'Change Photo' : 'Upload Photo'}</span>
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>
        
        {/* Business information form */}
        <div className="md:col-span-2">
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
            <div className="border-b border-gray-700 px-4 py-3">
              <h3 className="font-medium text-white">Rental Business Information</h3>
              <p className="text-xs text-gray-400 mt-1">
                Synced with multi-tenant Firebase Firestore
              </p>
              <div className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                <Building size={12} />
                {currentCompany.name} • ID: {companyId}
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Rental Name */}
              <div>
                <label htmlFor="rentalName" className="block text-sm font-medium text-gray-400 mb-2">
                  Rental Name * 
                  <span className="text-xs text-blue-400 ml-2">({currentCompany.name})</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={16} className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    id="rentalName"
                    name="rentalName"
                    value={profileData.rentalName || ''}
                    onChange={handleInputChange}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50"
                    placeholder={`Enter rental business name for ${currentCompany.name}`}
                    required
                    disabled={isSaving || !companyId}
                  />
                </div>
              </div>
              
              {/* Rental Address */}
              <div>
                <label htmlFor="rentalAddress" className="block text-sm font-medium text-gray-400 mb-2">
                  Rental Address
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                    <MapPin size={16} className="text-gray-500" />
                  </div>
                  <textarea
                    id="rentalAddress"
                    name="rentalAddress"
                    value={profileData.rentalAddress || ''}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Enter your rental business address"
                    disabled={isSaving || !companyId}
                  />
                </div>
              </div>

              {/* ⭐ Business Registration Number */}
              <div>
                <label htmlFor="businessRegistrationNumber" className="block text-sm font-medium text-gray-400 mb-2">
                  Business Registration Number
                  <span className="text-xs text-gray-500 ml-2">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FileText size={16} className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    id="businessRegistrationNumber"
                    name="businessRegistrationNumber"
                    value={profileData.businessRegistrationNumber || ''}
                    onChange={handleInputChange}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Enter business registration number (e.g., BRN123456)"
                    disabled={isSaving || !companyId}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  This will be displayed on invoices and official documents
                </p>
              </div>
              
              {/* Phone Numbers */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-400">
                    Phone Numbers
                  </label>
                  <button
                    type="button"
                    onClick={() => addField('phoneNumbers')}
                    className="text-orange-500 hover:text-orange-400 flex items-center gap-1 text-sm disabled:opacity-50"
                    disabled={isSaving || !companyId}
                  >
                    <Plus size={14} />
                    Add Number
                  </button>
                </div>
                
                {profileData.phoneNumbers && profileData.phoneNumbers.map((phone, index) => (
                  <div key={`phone-${index}`} className="flex gap-2 mb-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone size={16} className="text-gray-500" />
                      </div>
                      <input
                        type="tel"
                        value={phone || ''}
                        onChange={(e) => updateField('phoneNumbers', index, e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50"
                        placeholder="Enter phone number"
                        disabled={isSaving || !companyId}
                      />
                    </div>
                    {profileData.phoneNumbers && profileData.phoneNumbers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeField('phoneNumbers', index)}
                        className="p-2 bg-gray-700 hover:bg-red-900/40 text-gray-400 hover:text-red-400 rounded-lg transition-colors disabled:opacity-50"
                        disabled={isSaving || !companyId}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Email Addresses */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-400">
                    Email Addresses
                  </label>
                  <button
                    type="button"
                    onClick={() => addField('emails')}
                    className="text-orange-500 hover:text-orange-400 flex items-center gap-1 text-sm disabled:opacity-50"
                    disabled={isSaving || !companyId}
                  >
                    <Plus size={14} />
                    Add Email
                  </button>
                </div>
                
                {profileData.emails && profileData.emails.map((email, index) => (
                  <div key={`email-${index}`} className="flex gap-2 mb-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail size={16} className="text-gray-500" />
                      </div>
                      <input
                        type="email"
                        value={email || ''}
                        onChange={(e) => updateField('emails', index, e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50"
                        placeholder="Enter email address"
                        disabled={isSaving || !companyId}
                      />
                    </div>
                    {profileData.emails && profileData.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeField('emails', index)}
                        className="p-2 bg-gray-700 hover:bg-red-900/40 text-gray-400 hover:text-red-400 rounded-lg transition-colors disabled:opacity-50"
                        disabled={isSaving || !companyId}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Websites */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-400">
                    Websites
                  </label>
                  <button
                    type="button"
                    onClick={() => addField('websites')}
                    className="text-orange-500 hover:text-orange-400 flex items-center gap-1 text-sm disabled:opacity-50"
                    disabled={isSaving || !companyId}
                  >
                    <Plus size={14} />
                    Add Website
                  </button>
                </div>
                
                {profileData.websites && profileData.websites.map((website, index) => (
                  <div key={`website-${index}`} className="flex gap-2 mb-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Globe size={16} className="text-gray-500" />
                      </div>
                      <input
                        type="url"
                        value={website || ''}
                        onChange={(e) => updateField('websites', index, e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50"
                        placeholder="Enter website URL"
                        disabled={isSaving || !companyId}
                      />
                    </div>
                    {profileData.websites && profileData.websites.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeField('websites', index)}
                        className="p-2 bg-gray-700 hover:bg-red-900/40 text-gray-400 hover:text-red-400 rounded-lg transition-colors disabled:opacity-50"
                        disabled={isSaving || !companyId}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Save Button */}
              <div className="pt-4">
                <button
                  type="button"
                  onClick={saveProfile}
                  disabled={isSaving || isUploading || !companyId}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      <span>Saving to {currentCompany.name}...</span>
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      <span>Save Profile for {currentCompany.name}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;